// import { ref } from 'vue'
// import getGuestCartQuery from '~layers/app/graphql/cart/getGuestCart.gql'
// import getCustomerCartQuery from '~layers/app/graphql/cart/getCustomerCart.gql'
// import createEmptyCartMutation from '~layers/app/graphql/cart/createEmptyCart.gql'
// import addConfigurableProductsToCart from '~layers/app/graphql/cart/addConfigurableProductsToCart.gql'
// import addSimpleProductsToCart from '~layers/app/graphql/cart/addSimpleProductsToCart.gql'
// import updateCartItemsMutation from '~layers/app/graphql/cart/updateCartItems.gql'
// import removeItemFromCartMutation from '~layers/app/graphql/cart/removeItemFromCart.gql'
// import applyCouponToCartMutation from '~layers/app/graphql/cart/applyCouponToCart.gql'
// import setGuestEmailOnCartMutation from '~layers/app/graphql/cart/setGuestEmailOnCart.gql'
// import setBillingAddressOnCartMutation from '~layers/app/graphql/cart/setBillingAddressOnCart.gql'
// import setShippingAddressesOnCartMutation from '~layers/app/graphql/cart/setShippingAddressesOnCart.gql'
// import setShippingMethodsOnCartMutation from '~layers/app/graphql/cart/setShippingMethodsOnCart.gql'
// import placeOrderGraphqlMutation from '~layers/app/graphql/cart/placeOrder.gql'
// import setPaymentMethodOnCartMutation from '~layers/app/graphql/cart/setPaymentMethodOnCart.gql'
// import removeCouponFromCartMutation from '~layers/app/graphql/cart/removeCouponFromCart.gql'
// import mergeCartsMutation from '~layers/app/graphql/cart/mergeCarts.gql'
// import restoreCartMutationQuery from '~layers/app/graphql/cart/restoreCart.gql'
// import cartStatusQuery from '~layers/app/graphql/cart/cartStatus.gql'

import type {
  MagentoBillingAddress,
  MagentoCart, MagentoCartItem,
  MagentoCartItemInput,
  MagentoCartItemUpdateInput,
  MagentoPaymentMethod,
  MagentoPaymentMethodInput,
  MagentoSetBillingAddressOnCartInput,
  MagentoSetShippingAddressOnCartInput,
  MagentoShippingAddress,
  MagentoShippingMethod,
  MagentoProduct,
} from '~layers/app/types/magento'

export function useCart() {
  const cart = useState<MagentoCart | null>('cart', () => undefined)
  const cartId = useState<string | null>('cartId', () => null)
  const addedToCart = useState<Record<string, number>>('addedToCart', () => {})
  const pending = useState<boolean>('cartPending', () => false)
  const inactive = useState<boolean>('cartInactive', () => false)
  const watchersEnabled = useState<boolean>('cartWatchersEnabled', () => false)
  const loaded = useState<boolean>('cartLoaded', () => false)
  const errors = useState<string[]>('cartErrors', () => [])
  const errorsToSuppress: string[] = [
    'Sommige van de producten zijn niet op voorraad',
    'Some of the products are out of stock',
  ]

  const { public: config } = useRuntimeConfig()
  const cartCookieName = 'quote_masked_id_' + config.storeHeader

  const cartCookie = useCookie(cartCookieName, {
    maxAge: 60 * 60 * 24 * 60,
  })

  const placedOrderForQuoteCookie = useCookie('placed_order_for_quote', {
    maxAge: 60 * 60 * 10,
  })

  const setCartId = (token: string) => {
    cartCookie.value = token
    cartId.value = token
  }

  const getCartId = computed(() =>
    cart.value?.id || cartId.value || cartCookie.value,
  )

  const skus = computed<string[]>(() =>
    cart.value?.items.map(item => item.product.sku) || [],
  )

  const items = computed(() =>
    cart.value?.items || [],
  )

  const quantity = computed<number>(() =>
    cart.value?.items?.length || 0,
  )

  const sumQuantity = computed<number>(() =>
    cart.value?.total_quantity || 0,
  )

  const total = computed<number>(() =>
    cart.value?.prices?.grand_total.value || 0,
  )

  const skusWithQuantities = computed<Record<string, number>>(() =>
    cart.value?.items?.reduce((acc: Record<string, number>, item) => {
      acc[item.configured_variant?.sku || item.product.sku] = item.quantity
      return acc
    }, {}) || {},
  )

  const hasSuppressedErrors = computed<boolean>(() =>
    !!errorsToSuppress.filter(item => errors.value.includes(item)).length,
  )

  const getCartItemsWithErrors = computed(() =>
    items.value.filter(item => item.errors),
  )

  const hasCartItemErrors = computed<boolean>(() =>
    !!getCartItemsWithErrors.value.length,
  )

  const shippingCosts = computed<{ label: string, value: number } | null>(() => {
    let shipment = cart.value?.shipping_addresses ? cart.value.shipping_addresses[0] : null

    if (!shipment || !shipment.available_shipping_methods || !shipment.selected_shipping_method) {
      return null
    }
    shipment = shipment.available_shipping_methods.find(method => method.method_code === shipment.selected_shipping_method.method_code)

    if (!shipment) {
      return null
    }

    return {
      label: shipment.method_title,
      value: shipment.price_incl_tax.value,
    }
  })

  const shippingAddress = computed<MagentoShippingAddress | null>(() =>
    cart.value?.shipping_addresses?.[0]?.country?.code ? cart.value?.shipping_addresses[0] : null,
  )

  const billingAddress = computed<MagentoBillingAddress | null>(() =>
    cart.value?.billing_address,
  )

  const shippingAddressOrBillingAddress = computed<MagentoShippingAddress | MagentoBillingAddress | null>(() =>
    shippingAddress.value || billingAddress.value,
  )

  const shippingMethods = computed<MagentoShippingMethod[]>(() => cart.value?.shipping_addresses?.[0]?.available_shipping_methods?.map((method) => {
    const id = method.carrier_code + '_' + method.method_code
    return {
      ...method,
      id,
      type: null,
    }
  }))

  const crossSells = (subselect: MagentoCartItem[] | undefined): MagentoProduct[] => {
    return Object.values(
      (subselect || items.value).reduce((acc: Record<string, MagentoProduct>, item: MagentoCartItem) => {
        return {
          ...acc,
          ...(item.product.crosssell_products || []).reduce((acc2: Record<string, MagentoProduct>, product: MagentoProduct) => {
            return { ...acc2, [product.sku]: product }
          }, {}),
        }
      }, {}),
    )
  }

  const upSells = (subselect: MagentoCartItem[] | undefined): MagentoProduct[] => {
    return Object.values(
      (subselect || items.value).reduce((acc: Record<string, MagentoProduct>, item: MagentoCartItem) => {
        return {
          ...acc,
          ...(item.product.upsell_products || []).reduce((acc2: Record<string, MagentoProduct>, product: MagentoProduct) => {
            return { ...acc2, [product.sku]: product }
          }, {}),
        }
      }, {}),
    )
  }

  const selectedShippingMethod = computed<MagentoShippingMethod | null>(() => {
    const method = cart.value?.shipping_addresses?.[0]?.selected_shipping_method
    if (!method) {
      return null
    }
    const id = method.carrier_code + '_' + method.method_code
    return shippingMethods.value.find(item => item.id === id)
  })

  const selectedPaymentMethod = computed<MagentoPaymentMethod | null>(() =>
    cart.value?.selected_payment_method || null,
  )

  const handleAddedToCart = (compareWith?: Record<string, number>) => {
    if (!compareWith) {
      addedToCart.value = {}
      return
    }
    const diff: Record<string, number> = {}
    for (const i in skusWithQuantities.value) {
      if (!compareWith[i] || compareWith[i] < skusWithQuantities.value[i]) {
        diff[i] = skusWithQuantities.value[i] - (compareWith[i] || 0)
      }
    }
    addedToCart.value = diff
  }

  const handleErrors = (incomingErrors: Array<{ message: string }>) => {
    if (!incomingErrors || incomingErrors.length === 0) {
      console.log('clear errors', incomingErrors)
      errors.value = []
    }
    else {
      console.log('handle Errors', incomingErrors)
      errors.value = incomingErrors.map(item => item.message)
    }
  }

  const email = computed<string | null>(() =>
    cart.value?.email || null,
  )

  const reset = async () => {
    cartId.value = null
    cartCookie.value = null
    cart.value = null
  }

  const getCart = async (options?: { token?: string, force?: boolean }) => {
    if (pending.value) {
      return
    }
    if (cart.value && !options?.force) {
      return
    }

    pending.value = true
    setCartId(options?.token || cartCookie.value || '')

    await getGuestCart()
    pending.value = false
  }

  const getGuestCart = async () => {
    if (!cartId.value) {
      cart.value = undefined
      await createEmptyCart()
      return
    }

    const result = await useGraphqlQuery('getGuestCart', { cartId: cartId.value }, useGraphqlCustomerClient())

    if (result.data?.cart) {
      setCartData(result.data.cart)
    }

    if (result.errors?.length) {
      console.log('getGuestCart errors', result.errors)

      if (result.errors.some((error: { message: string }) => error.message === 'The cart isn\'t active.')) {
        inactive.value = true
      }
      else {
        await createEmptyCart()
      }
    }
  }

  const setCartData = (cartData: Record<string, unknown> | undefined) => {
    if (cartData) {
      cart.value = {
        ...cartData,
      }
    }
  }

  const cartStatus = async (cartId: string) => {
    const result = await useGraphqlQuery('getCartStatus', { cartId }, useGraphqlCustomerClient())

    if (result.errors?.length) {
      console.log('cartStatus errors', result.errors)
    }

    return result.data?.cartStatus || null
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getCustomerCart = async () => {
    const result = await useGraphqlQuery('getCustomerCart', null, useGraphqlCustomerClient())
    if (result.data?.cart) {
      setCartData(result.data.cart)
    }

    if (result.errors?.length) {
      console.log('getCustomerCart errors', result.errors)
    }

    if (placedOrderForQuoteCookie.value && placedOrderForQuoteCookie.value !== result.data.cart.id) {
      inactive.value = true
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const mergeCarts = async () => {
    if (!cartCookie.value) {
      return
    }

    const result = await useGraphqlMutation('mergeCarts', {
      guestCardId: cartCookie.value,
      customerCartId: getCartId.value,
    }, useGraphqlCustomerClient())

    if (result.data?.mergeCarts) {
      cartCookie.value = null
      setCartData(result?.data?.mergeCarts)
      handleErrors([])
    }

    if (result.errors?.length) {
      handleErrors(result.errors)
    }
  }

  const createEmptyCart = async () => {
    const result = await useGraphqlMutation('createEmptyCart', null, useGraphqlCustomerClient())

    if (!result.errors?.length) {
      cart.value = undefined
      setCartId(result.data.createEmptyCart)
      loaded.value = true
    }
  }

  const addToCart = async (cartItems: MagentoCartItemInput[], suppress: boolean | undefined) => {
    pending.value = true

    if (!getCartId.value) {
      await createEmptyCart()
    }

    if (!getCartId.value) {
      console.error('No cart id')
      return
    }

    const currentCartContents = { ...skusWithQuantities.value }
    const configurableItemsToAdd = cartItems.filter(item => item.parent_sku)
    const simpleItemsToAdd = cartItems.filter(item => !item.parent_sku)

    const addProductsToCartMutations = []
    if (simpleItemsToAdd.length) {
      addProductsToCartMutations.push(
        await useGraphqlMutation('addSimpleProductsToCart', {
          input: {
            cart_id: getCartId.value,
            cart_items: simpleItemsToAdd,
          },
        }, useGraphqlCustomerClient()),
      )
    }
    if (configurableItemsToAdd.length) {
      addProductsToCartMutations.push(
        await useGraphqlMutation('addConfigurableProductsToCart', {
          input: {
            cart_id: getCartId.value,
            cart_items: configurableItemsToAdd,
          },
        }, useGraphqlCustomerClient()),
      )
    }

    const promiseResults = await Promise.all(addProductsToCartMutations)
    console.log('promiseResults', promiseResults)
    const result = promiseResults.pop()
    if (result.data?.addToCart) {
      setCartData(result?.data?.addToCart?.cart)
      handleAddedToCart(suppress ? undefined : currentCartContents)
      handleErrors([])
    }

    if (result.errors?.length) {
      handleAddedToCart(suppress ? undefined : currentCartContents)
      handleErrors(result.errors)
    }

    pending.value = false
  }

  const updateCartItem = async (cartItem: MagentoCartItemUpdateInput, suppress: boolean | undefined) => {
    if (!getCartId.value) {
      return
    }

    const currentCartContents = { ...skusWithQuantities.value }

    pending.value = true

    const result = await useGraphqlMutation('updateCartItems', {
      input: {
        cart_id: getCartId.value,
        cart_items: [cartItem],
      },
    }, useGraphqlCustomerClient())

    if (result?.data?.updateCartItems) {
      setCartData(result?.data?.updateCartItems?.cart)

      handleAddedToCart(suppress ? undefined : currentCartContents)
    }

    if (result.errors?.length) {
      handleErrors(result.errors)
    }

    pending.value = false
  }

  const removeCartItem = async (cartItemId: number) => {
    if (!getCartId.value) {
      return
    }

    pending.value = true

    const result = await useGraphqlMutation('removeItemFromCart', {
      input: {
        cart_id: getCartId.value,
        cart_item_id: cartItemId,
      },
    }, useGraphqlCustomerClient())

    if (result.data?.removeItemFromCart) {
      setCartData(result?.data?.removeItemFromCart?.cart)
      handleAddedToCart()
    }

    if (result.errors?.length) {
      handleErrors(result.errors)
    }

    pending.value = false
  }

  const applyCouponToCart = async (couponCode: string) => {
    if (!getCartId.value) {
      return
    }

    pending.value = true

    const result = await useGraphqlMutation('applyCouponToCart', {
      input: {
        cart_id: getCartId.value,
        coupon_code: couponCode,
      },
    }, useGraphqlCustomerClient())

    if (result?.data?.applyCouponToCart) {
      setCartData(result.data?.applyCouponToCart?.cart)
      handleErrors([])
    }

    if (result.errors?.length) {
      handleErrors(result.errors)
    }

    pending.value = false
  }

  const removeCouponFromCart = async () => {
    if (!getCartId.value) {
      return
    }

    pending.value = true

    const result = await useGraphqlMutation('removeCouponFromCart', {
      input: {
        cart_id: getCartId.value,
      },
    }, useGraphqlCustomerClient())

    if (result.data?.removeCouponFromCart) {
      setCartData(result?.data?.removeCouponFromCart?.cart)
      handleErrors([])
    }

    if (result.errors?.length) {
      handleErrors(result.errors)
    }

    pending.value = false
  }

  const addEmailAddressLoading = ref<boolean>(false)
  const addEmailAddressCalled = ref<boolean>(false)
  const addEmailAddressError = ref()
  const addEmailAddress = async (email: string) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true
    addEmailAddressLoading.value = true
    addEmailAddressCalled.value = true

    const result = await useGraphqlMutation('setGuestEmailOnCart', {
      input: {
        cart_id: getCartId.value,
        email,
      },
    }, useGraphqlCustomerClient())

    setCartData(result.data?.cart?.cart)

    if (result.errors?.length) {
      addEmailAddressError.value = result.errors
      handleErrors(result.errors)
    }

    pending.value = false
    addEmailAddressLoading.value = false
  }

  const addBillingAddressLoading = ref<boolean>(false)
  const addBillingAddressCalled = ref<boolean>(false)
  const addBillingAddressError = ref()
  const addBillingAddress = async (address: MagentoSetBillingAddressOnCartInput) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true
    addBillingAddressLoading.value = true
    addBillingAddressCalled.value = true

    const result = await useGraphqlMutation('setBillingAddressOnCart', {
      input: {
        cart_id: getCartId.value,
        billing_address: address,
      },
    }, useGraphqlCustomerClient())

    setCartData(result?.data?.cart?.cart)

    if (result.errors?.length) {
      addBillingAddressError.value = result.errors
      handleErrors(result.errors)
    }

    pending.value = false
    addBillingAddressLoading.value = false
  }

  const addShippingAddressLoading = ref<boolean>(false)
  const addShippingAddressCalled = ref<boolean>(false)
  const addShippingAddressError = ref()
  const addShippingAddress = async (address: MagentoSetShippingAddressOnCartInput) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true
    addShippingAddressLoading.value = true
    addShippingAddressCalled.value = true

    const result = await useGraphqlMutation('setShippingAddressesOnCart', {
      input: {
        cart_id: getCartId.value,
        shipping_addresses: [address],
      },
    }, useGraphqlCustomerClient())

    setCartData(result?.data?.cart?.cart)

    if (result?.errors?.length) {
      addShippingAddressError.value = result.errors
      handleErrors(result.errors)
    }

    pending.value = false
    addShippingAddressLoading.value = false
  }

  const addShippingMethodLoading = ref<boolean>(false)
  const addShippingMethodCalled = ref<boolean>(false)
  const addShippingMethodError = ref()
  const addShippingMethod = async (carrierCode: string, methodCode: string, extraData: Record<string, unknown> | undefined) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true
    addShippingMethodLoading.value = true
    addShippingMethodCalled.value = true

    const result = await useGraphqlMutation('setShippingMethodsOnCart', {
      input: {
        cart_id: getCartId.value,
        shipping_methods: [{
          carrier_code: carrierCode,
          method_code: methodCode,
        }],
        ...(extraData !== undefined ? extraData : {}),
      },
    }, useGraphqlCustomerClient())

    setCartData(result?.data?.cart?.cart)

    if (result?.errors?.length) {
      addShippingMethodError.value = result.errors
      handleErrors(result.errors)
    }

    pending.value = false
    addShippingMethodLoading.value = false
  }

  const addPaymentMethodLoading = ref<boolean>(false)
  const addPaymentMethodCalled = ref<boolean>(false)
  const addPaymentMethodError = ref()
  const addPaymentMethod = async (paymentMethod: MagentoPaymentMethodInput) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true
    addPaymentMethodLoading.value = true
    addPaymentMethodCalled.value = true

    const result = await useGraphqlMutation('setPaymentMethodOnCart', {
      input: {
        cart_id: getCartId.value,
        payment_method: paymentMethod,
      },
    }, useGraphqlCustomerClient())

    setCartData(result?.data?.cart?.cart)

    if (result?.errors?.length) {
      addPaymentMethodError.value = result.errors
      handleErrors(result.errors)
    }

    pending.value = false
    addPaymentMethodLoading.value = false
  }

  const placeOrderLoading = ref<boolean>(false)
  const placeOrderCalled = ref<boolean>(false)
  const placeOrderError = ref<boolean>(false)
  const placeOrderErrorMessage = ref<string>('')
  const placeOrder = async () => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true
    placeOrderLoading.value = true
    placeOrderCalled.value = true
    placeOrderError.value = false
    placeOrderErrorMessage.value = ''

    const result = await useGraphqlMutation('placeOrder', {
      input: {
        cart_id: getCartId.value,
      },
    }, useGraphqlCustomerClient())

    placeOrderError.value = !!result?.errors?.length || !!result?.data?.placeOrder?.errors?.length
    if (placeOrderError.value) {
      placeOrderErrorMessage.value = result?.data?.placeOrder?.errors?.[0]?.message || 'Unexpected Error'
      handleErrors(result?.data?.placeOrder?.errors || result?.errors)
    }

    placedOrderForQuoteCookie.value = getCartId.value

    pending.value = false
    placeOrderLoading.value = false

    return result?.data?.placeOrder?.order || result?.data?.placeOrder?.orderV2?.number
  }

  const restoreCart = async (cartId: string) => {
    if (!getCartId.value) {
      throw new Error('There is no cart')
    }
    pending.value = true

    const result = await useGraphqlMutation('restoreCart', {
      cartId,
    }, useGraphqlCustomerClient())

    if (result?.errors?.length) {
      handleErrors(result.errors)
      pending.value = false
      return null
    }

    pending.value = false

    return result
  }

  const initCartWatchers = () => {
    if (watchersEnabled.value) return
    watchersEnabled.value = true

    watch(cart, (newVal, oldVal) => {
      if (!oldVal && newVal) {
        loaded.value = true
      }
    })
  }

  onMounted(() => {
    const { query } = useRoute()
    getCart({ token: query?.masked_id as string || undefined })
  })

  return {
    cart,
    reset,
    email,
    cartId,
    getCartId,
    cartCookieName,
    pending,
    loaded,
    quantity,
    sumQuantity,
    getCart,
    setCartId,
    setCartData,
    addedToCart,
    errors,
    total,
    addToCart,
    updateCartItem,
    removeCartItem,
    skus,
    items,
    hasSuppressedErrors,
    getCartItemsWithErrors,
    hasCartItemErrors,
    shippingCosts,
    applyCouponToCart,
    removeCouponFromCart,
    shippingAddressOrBillingAddress,
    billingAddress,
    shippingAddress,
    addEmailAddress,
    addEmailAddressLoading,
    addEmailAddressCalled,
    addEmailAddressError,
    addBillingAddress,
    addBillingAddressLoading,
    addBillingAddressCalled,
    addBillingAddressError,
    addShippingAddress,
    addShippingAddressLoading,
    addShippingAddressCalled,
    addShippingAddressError,
    shippingMethods,
    selectedShippingMethod,
    addShippingMethod,
    addShippingMethodError,
    addShippingMethodLoading,
    addShippingMethodCalled,
    selectedPaymentMethod,
    addPaymentMethod,
    addPaymentMethodError,
    addPaymentMethodLoading,
    addPaymentMethodCalled,
    placeOrder,
    placeOrderError,
    placeOrderErrorMessage,
    placeOrderLoading,
    placeOrderCalled,
    crossSells,
    upSells,
    inactive,
    createEmptyCart,
    restoreCart,
    cartStatus,
    placedOrderForQuoteCookie,
    initCartWatchers,
  }
}
